import { getMapCoordinates } from './mapCoordinates';
import { getEventRiskLabel, getEventRiskScore } from './riskEventFeed';
import { getRiskHexColor } from './mapUtils';
import { countryReportDayWindow, countryReportTimeZone } from '../../functions/src/common/countryReportTime';

const DAY_MS = 86_400_000;

function timestampMillis(value: any): number | null {
    if (value == null)
        return null;
    if (typeof value.toMillis === 'function')
        return value.toMillis();
    if (typeof value.toDate === 'function')
        return value.toDate().getTime();
    if (typeof value.seconds === 'number')
        return value.seconds * 1000;
    if (typeof value._seconds === 'number')
        return value._seconds * 1000;
    const millis = typeof value === 'number' ? value : Date.parse(String(value));
    return Number.isFinite(millis) ? millis : null;
}

export function dailyMicroWindow(report: any): { start: number; end: number } | null {
    if (!report?.reportArea && typeof report?.reportDate === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(report.reportDate)) {
        try {
            const timeZone = report.timeZone || countryReportTimeZone(report.iso3 || report.countryCode || '');
            const window = countryReportDayWindow(report.reportDate, timeZone);
            return { start: window.start, end: window.end - 1 };
        } catch { /* Older reports may lack a country code. */ }
    }
    const explicitStart = timestampMillis(report?.windowStart);
    const explicitEnd = timestampMillis(report?.windowEnd ?? report?.generatedAt);
    if (explicitStart !== null && explicitEnd !== null && explicitStart <= explicitEnd)
        return { start: explicitStart, end: explicitEnd };

    const reportDate = typeof report?.reportDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(report.reportDate)
        ? Date.parse(`${report.reportDate}T00:00:00.000Z`)
        : null;
    const anchor = reportDate ?? timestampMillis(
        report?.analysis_date ?? report?.date ?? report?.lastUpdated ?? report?.timestamp ?? report?.generatedAt
    );
    if (anchor === null)
        return null;
    const start = Date.parse(`${new Date(anchor).toISOString().slice(0, 10)}T00:00:00.000Z`);
    return { start, end: start + DAY_MS - 1 };
}

export function dailyMicroEventTime(event: any): number | null {
    return timestampMillis(
        event?.publishedAt ?? event?.pubDate ?? event?.eventDate ?? event?.occurredAt ??
        event?.date ?? event?.processedAt ?? event?.createdAt ?? event?.updatedAt
    );
}

export function filterDailyMicroEvents(report: any, events: any[]): any[] {
    const window = dailyMicroWindow(report);
    if (!window)
        return [];
    const seen = new Set<string>();
    const matches: any[] = [];
    for (let i = 0; i < events.length; i++) {
        const event = events[i];
        const time = dailyMicroEventTime(event);
        if (time === null || time < window.start || time > window.end)
            continue;
        const identity = String(event?.id ?? `${event?.title ?? event?.name ?? ''}|${time}|${event?.lat ?? event?.latitude ?? ''}|${event?.lng ?? event?.longitude ?? ''}`);
        if (seen.has(identity))
            continue;
        seen.add(identity);
        matches.push(event);
    }
    return matches.sort((left, right) => (dailyMicroEventTime(right) ?? 0) - (dailyMicroEventTime(left) ?? 0));
}

export function dailyMicroEventMarker(event: any, index: number) {
    const coordinates = getMapCoordinates(event);
    if (!coordinates)
        return null;
    const vetted = event.verified === true && event.vettingStatus === 'approved' && event.generatedIncident !== true;
    const riskScore = vetted ? getEventRiskScore(event) : null;
    const riskLevel = vetted ? getEventRiskLabel(event) : null;
    const color = riskScore === null && !riskLevel ? '#64748b' : getRiskHexColor(riskScore ?? riskLevel);
    return {
        id: event.id ?? `alert-${index}`,
        ...coordinates,
        name: event.title ?? event.summary ?? 'Risk Event',
        title: event.title ?? event.summary ?? 'Risk Event',
        type: event.category ?? event.type ?? 'Alert',
        category: event.category,
        summary: event.summary ?? event.description ?? event.title ?? '',
        date: dailyMicroEventTime(event),
        riskScore,
        riskLevel,
        methodology: vetted ? event.methodology || event.riskJustification : null,
        scoreMethod: vetted ? event.scoreMethod : null,
        confidencePercent: vetted ? event.confidencePercent : null,
        confidence_percentage: vetted ? event.confidence_percentage : null,
        confidenceScore: vetted ? event.confidenceScore : null,
        confidence_score: vetted ? event.confidence_score : null,
        confidenceLevel: vetted ? event.confidenceLevel : null,
        confidenceJustification: vetted ? event.confidenceJustification : null,
        analystConfidence: vetted ? event.analystConfidence : null,
        confidence: vetted ? event.confidence : null,
        vettingMethod: event.vettingMethod,
        vettingVersion: event.vettingVersion,
        verification_statement: vetted ? event.verification_statement : null,
        color,
        isAlert: true,
    };
}
